import { createSelector } from 'reselect';
// Import services.
import {
	AddressService,
} from '@app/services';
// Import selectors.
import {
	makeGetNewBusinessApplicationSelector,
} from '@app/redux/selectors'
// Import states.
import {
	AppState,
} from '@app/redux/states';
// Import selectors.
import {
	getNewBusinessApplicationState,
} from '@app/redux/selectors/base'
// Import states.
import {
	NewBusinessApplicationState,
} from '@app/redux/states';
// Import api models.
import {
	Address,
	AddressLocation,
    AddressLocationWithAccuracy,
	NewBusinessApplication,
	BuildingLimitsViewSummary,
	BuildingRiskDataCommercial,
	BuildingResponseCommercialExistingWithStatus,
} from '@app/models/api';
// Import enumerations.
import {
	RecordStatus,
} from '@app/enumerations';
// Import props.
import {
	BuildingProps,
} from '@app/props';

// Fetches the building guid from the props.
const getBuildingGuid = (state: AppState, props: BuildingProps): string => {
	return props.buildingGuid;
}

// Get New Business Buildings Commercial Selector.
const makeGetNewBusinessBuildingsCommercialSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): BuildingResponseCommercialExistingWithStatus[] => {
		return newBusinessApplication !== undefined
			? newBusinessApplication.risks
			: [];
	}
);

// Get New Business Building Limits View Summaries Commercial Selector.
const makeGetNewBusinessBuildingLimitsViewSummariesCommercialSelector = () => createSelector(
	[makeGetNewBusinessBuildingsCommercialSelector()],
	(buildingsCommercial: BuildingResponseCommercialExistingWithStatus[]): BuildingLimitsViewSummary[] => {
		return buildingsCommercial.map((buildingCommercial: BuildingResponseCommercialExistingWithStatus) => {
			let buildingLimitsViewSummary: BuildingLimitsViewSummary = new BuildingLimitsViewSummary();
			buildingLimitsViewSummary.buildingGuid = buildingCommercial.id;
			buildingLimitsViewSummary.buildingDescription = buildingCommercial.buildingData?.buildingDescription ?? null;
			buildingLimitsViewSummary.buildingReplacementCostValue = buildingCommercial.buildingData?.buildingReplacementCostValue ?? null;
			buildingLimitsViewSummary.buildingAddress = AddressService.getInstance().formatAddressToSingleLineString(buildingCommercial.address?.address ?? new Address());
			buildingLimitsViewSummary.building = buildingCommercial.limits?.building ?? null;
			buildingLimitsViewSummary.contents = buildingCommercial.limits?.contents ?? null;
			buildingLimitsViewSummary.businessIncomeExtraExpense = buildingCommercial.limits?.businessIncomeExtraExpense ?? null;

			return buildingLimitsViewSummary;
		});
	}
);

// Get New Business Building Commercial Selector.
const makeGetNewBusinessBuildingCommercialSelector = () => createSelector(
	[getBuildingGuid, makeGetNewBusinessBuildingsCommercialSelector()],
	(buildingGuid: string, buildingsCommercial: BuildingResponseCommercialExistingWithStatus[]): BuildingResponseCommercialExistingWithStatus | undefined=> {
		return buildingsCommercial.find((buildingCommercial: BuildingResponseCommercialExistingWithStatus) => buildingCommercial.id === buildingGuid);
	}
);

// Get New Business Building Risk Data Commercial Selector.
const makeGetNewBusinessBuildingRiskDataCommercialSelector = () => createSelector(
	[makeGetNewBusinessBuildingCommercialSelector()],
	(buildingCommercial: BuildingResponseCommercialExistingWithStatus | undefined): BuildingRiskDataCommercial | undefined => {
		return buildingCommercial !== undefined && buildingCommercial.buildingData !== null
			? buildingCommercial.buildingData
			: undefined;
	}
);

// Get New Business Building Address Location Commercial Selector.
const makeGetNewBusinessBuildingAddressLocationCommercialSelector = () => createSelector(
	[makeGetNewBusinessBuildingCommercialSelector()],
	(buildingCommercial: BuildingResponseCommercialExistingWithStatus | undefined): AddressLocationWithAccuracy | undefined => {
		return buildingCommercial !== undefined && buildingCommercial.address !== null
			? buildingCommercial.address
			: undefined;
	}
);

// Get New Business Building Address Location Commercial Without Accuracy Selector.
const makeGetNewBusinessBuildingAddressLocationCommercialWithoutAccuracySelector = () => createSelector(
	[makeGetNewBusinessBuildingAddressLocationCommercialSelector()],
	(addressLocation: AddressLocationWithAccuracy | undefined): AddressLocation | undefined => {
		let addressLocationWithoutAccuracy: AddressLocation | undefined = undefined;
		if (addressLocation !== undefined) {
			addressLocationWithoutAccuracy = {
				address: addressLocation.address,
				gpsCoordinates: addressLocation.gpsCoordinates,
			};
		}
		return addressLocationWithoutAccuracy;
	}
);

// Get New Business Building Address Commercial Selector.
const makeGetNewBusinessBuildingAddressCommercialSelector = () => createSelector(
	[makeGetNewBusinessBuildingAddressLocationCommercialSelector()],
	(buildingAddress: AddressLocationWithAccuracy | undefined): Address | undefined => {
		return buildingAddress !== undefined && buildingAddress.address !== null
			? buildingAddress.address
			: undefined;
	}
);

// Get New Business Building Address Line Commercial Selector.
const makeGetNewBusinessBuildingAddressLineCommercialSelector = () => createSelector(
	[makeGetNewBusinessBuildingAddressCommercialSelector()],
	(address: Address | undefined): string | undefined => {
		return address !== undefined
			? AddressService.getInstance().formatAddressToSingleLineString(address)
			: undefined;
	}
);

// Get Selected New Business Building Guid Selector.
const makeGetNewBusinessSelectedBuildingGuidSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): string | undefined => {
		return newBusinessApplicationState !== undefined
			? newBusinessApplicationState.selectedBuildingGuid
			: undefined;
	}
);

// Are All New Business Buildings Commercial Complete Selector.
const makeAreAllNewBusinessBuildingsCommercialCompleteSelector = () => createSelector(
	[makeGetNewBusinessBuildingsCommercialSelector()],
	(buildingsCommercial: BuildingResponseCommercialExistingWithStatus[]): boolean => {
		return buildingsCommercial.every((buildingCommercial: BuildingResponseCommercialExistingWithStatus) => {
			return buildingCommercial.status?.statusCode === RecordStatus.COMPLETE
		})
	}
);


// Export composed methods.
export {
	
};

// export selectors.
export {
	makeGetNewBusinessBuildingsCommercialSelector,
	makeGetNewBusinessBuildingLimitsViewSummariesCommercialSelector,
	makeGetNewBusinessBuildingCommercialSelector,
	makeGetNewBusinessBuildingRiskDataCommercialSelector,
	makeGetNewBusinessBuildingAddressLocationCommercialSelector,
	makeGetNewBusinessBuildingAddressLocationCommercialWithoutAccuracySelector,
	makeGetNewBusinessBuildingAddressCommercialSelector,
	makeGetNewBusinessBuildingAddressLineCommercialSelector,
	makeGetNewBusinessSelectedBuildingGuidSelector,
	makeAreAllNewBusinessBuildingsCommercialCompleteSelector,
};